import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaWifi, FaList, FaClock, FaSearch, FaPhone, FaSync, FaTimes, FaHistory, FaKey, FaCopy, FaInbox, FaMobileAlt } from 'react-icons/fa';
import './WifiAdminPanel.css';

function WifiAdminPanel({ onClose }) {
  const [tokens, setTokens] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tokens');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedToken, setSelectedToken] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [tokensRes, activityRes] = await Promise.all([
        axios.get('/api/wifi/admin/tokens'),
        axios.get('/api/wifi/admin/activity')
      ]);

      if (tokensRes.data.success) {
        setTokens(tokensRes.data.tokens);
      }

      if (activityRes.data.success) {
        setActivities(activityRes.data.activities);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error cargando datos:', error);
      setLoading(false);
    }
  };

  const handleSyncLinks = async () => {
    try {
      setSyncing(true);
      setSyncSummary(null);
      const response = await axios.post('/api/wifi/admin/sync-links', {
        startId: 100,
        endId: 1000
      });

      if (response.data.success) {
        setSyncSummary(response.data.summary);
        await loadData();
      } else {
        alert(response.data.error || 'No se pudo sincronizar los links');
      }
    } catch (error) {
      console.error('Error sincronizando links:', error);
      alert('Error sincronizando links');
    } finally {
      setSyncing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copiado al portapapeles');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShowHistory = (token) => {
    setSelectedToken(token);
    setShowHistoryModal(true);
  };

  const filteredTokens = tokens.filter(token =>
    token.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    token.clientId?.toString().includes(searchTerm) ||
    token.phoneNumber?.includes(searchTerm)
  );

  const filteredActivities = activities.filter(activity =>
    activity.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.clientId?.toString().includes(searchTerm) ||
    activity.phoneNumber?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="wifi-admin-overlay">
        <div className="wifi-admin-modal">
          <div className="wifi-admin-loading">
            <div className="spinner"></div>
            <p>Cargando informacion WiFi...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wifi-admin-overlay" onClick={onClose}>
      <div className="wifi-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wifi-admin-header">
          <h2><FaWifi /> Gestión WiFi - Panel Administrativo</h2>
          <div className="header-buttons">
            <button onClick={loadData} className="refresh-button">
              <FaSync /> Actualizar
            </button>
            <button onClick={handleSyncLinks} className="refresh-button" disabled={syncing}>
              <FaSync /> {syncing ? 'Sincronizando...' : 'Sincronizar 100-1000'}
            </button>
            <button onClick={onClose} className="close-button">
          <FaTimes />
            </button>
          </div>
        </div>

        <div className="wifi-admin-tabs">
          <button
            className={`tab-button ${activeTab === 'tokens' ? 'active' : ''}`}
            onClick={() => setActiveTab('tokens')}
          >
            <FaList /> Links Generados ({tokens.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <FaClock /> Actividad Reciente ({activities.length})
          </button>
        </div>

        <div className="wifi-admin-search">
          <input
            type="text"
            placeholder="Buscar por nombre, ID o telefono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {syncSummary && (
          <div className="wifi-admin-content">
            <div className="activity-item" style={{ marginBottom: '12px' }}>
              <div className="activity-details">
                <div className="activity-client">
                  <strong>Sincronización completada</strong>
                  <span className="activity-id">
                    IDs {syncSummary.startId}-{syncSummary.endId}
                  </span>
                </div>
                <div className="activity-info">
                  <span className="activity-ssid">Procesados: {syncSummary.procesados}</span>
                  <span className="activity-phone">Links: {syncSummary.conLink} (nuevos {syncSummary.linksGenerados})</span>
                </div>
                <div className="activity-info">
                  <span className="activity-ssid">ONUs: {syncSummary.conONU} | Sin ONU: {syncSummary.sinONU}</span>
                  <span className="activity-phone">IDs actualizados: {syncSummary.deviceIdsActualizados}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tokens' ? (
          <div className="wifi-admin-content">
            <div className="tokens-grid">
              {filteredTokens.map((token) => (
                <div 
                  key={token.token} 
                  className="token-card"
                  onClick={() => handleShowHistory(token)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="token-header">
                    <div className="client-info">
                      <h3>{token.clientName}</h3>
                      <span className="client-id">ID: {token.clientId}</span>
                    </div>
                    <span className="client-phone">{token.phoneNumber}</span>
                  </div>
                  
                  <div className="token-body">
                    <div className="token-link-container">
                      <input
                        type="text"
                        value={token.link}
                        readOnly
                        className="token-link-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(token.link);
                        }}
                        className="copy-button"
                        title="Copiar link"
                      >
                <FaCopy />
                      </button>
                    </div>

                    {token.ssid && (
                      <div className="token-wifi-info">
                        <div className="wifi-field">
                          <strong>SSID:</strong>
                          <span>{token.ssid}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(token.ssid);
                            }}
                            className="copy-small-button"
                          >
                    <FaCopy />
                          </button>
                        </div>
                      </div>
                    )}

                    {token.password && (
                      <div className="token-wifi-info">
                        <div className="wifi-field">
                          <strong>Contrasena:</strong>
                          <span className="password-text">{token.password}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(token.password);
                            }}
                            className="copy-small-button"
                          >
                    <FaCopy />
                          </button>
                        </div>
                      </div>
                    )}

                    {typeof token.connectedDevices !== 'undefined' && (
                      <div className="token-wifi-info">
                        <div className="wifi-field connected-devices-field">
                          <strong><FaMobileAlt /> Dispositivos:</strong>
                          <span className={token.connectedDevices > 0 ? 'devices-count active' : 'devices-count'}>
                            {token.connectedDevices} conectado{token.connectedDevices !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="token-dates">
                      <div className="token-date">
                        <span className="date-label">Creado:</span>
                        <span className="date-value">{formatDate(token.createdAt)}</span>
                      </div>
                      {token.lastUsed && (
                        <div className="token-date">
                          <span className="date-label">Ultimo uso:</span>
                          <span className="date-value">{formatDate(token.lastUsed)}</span>
                        </div>
                      )}
                      {token.lastUpdated && (
                        <div className="token-date">
                          <span className="date-label">Ultimo cambio:</span>
                          <span className="date-value">{formatDate(token.lastUpdated)}</span>
                        </div>
                      )}
                    </div>

                    {token.changeHistory && token.changeHistory.length > 0 && (
                      <div className="history-badge">
                         {token.changeHistory.length} cambio{token.changeHistory.length !== 1 ? 's' : ''} registrado{token.changeHistory.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {filteredTokens.length === 0 && (
              <div className="empty-state">
                <p>No se encontraron tokens que coincidan con la busqueda</p>
              </div>
            )}
          </div>
        ) : (
          <div className="wifi-admin-content">
            <div className="activity-list">
              {filteredActivities.map((activity, index) => (
                <div key={`${activity.token}-${index}`} className="activity-item">
                  <div className="activity-icon"></div>
                  <div className="activity-details">
                    <div className="activity-client">
                      <strong>{activity.clientName}</strong>
                      <span className="activity-id">ID: {activity.clientId}</span>
                    </div>
                    <div className="activity-info">
                      <span className="activity-ssid"> {activity.ssid}</span>
                      <span className="activity-phone"> {activity.phoneNumber}</span>
                    </div>
                    <div className="activity-date">
                      {formatDate(activity.changedAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredActivities.length === 0 && (
              <div className="empty-state">
                <p>No hay actividad reciente registrada</p>
              </div>
            )}
          </div>
        )}

        {showHistoryModal && selectedToken && (
          <div className="history-modal-overlay" onClick={() => setShowHistoryModal(false)}>
            <div className="history-modal" onClick={(e) => e.stopPropagation()}>
              <div className="history-modal-header">
                <h3> <FaHistory /> Historial de Cambios</h3>
                <button onClick={() => setShowHistoryModal(false)} className="close-history-button">
                  <FaTimes />
                </button>
              </div>
              
              <div className="history-modal-info">
                <h4>{selectedToken.clientName}</h4>
                <p>ID: {selectedToken.clientId} |  {selectedToken.phoneNumber}</p>
              </div>

              <div className="history-modal-content">
                {selectedToken.changeHistory && selectedToken.changeHistory.length > 0 ? (
                  <div className="history-timeline">
                    {[...selectedToken.changeHistory].reverse().map((change, index) => (
                      <div key={index} className="history-item">
                        <div className="history-marker"></div>
                        <div className="history-details">
                          <div className="history-date">
                            {formatDate(change.changedAt)}
                          </div>
                          {change.ssid && (
                            <div className="history-field">
                              <strong>SSID:</strong> {change.ssid}
                            </div>
                          )}
                          {change.password && (
                            <div className="history-field">
                              <strong>Contrasena:</strong> 
                              <span className="password-text">{change.password}</span>
                              <button
                                onClick={() => copyToClipboard(change.password)}
                                className="copy-history-button"
                              >
                                
                              </button>
                            </div>
                          )}
                          <div className="history-meta">
                            Cambiado por: {change.changedBy || 'cliente'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-history">
                    <p>No hay cambios registrados para este cliente</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WifiAdminPanel;
